import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    collection,
    addDoc,
    doc,
    getDoc,
    updateDoc,
    getDocs,
    query,
    orderBy,
} from "firebase/firestore";
import { db } from "../../../firebase";
import { useUserAuth } from "../../../context/userAuthContext";
import {
    ChevronLeft,
    Save,
    X,
    DollarSign,
    Calendar,
    Globe,
    Bell,
    AlertCircle,
    CheckCircle,
    Loader2,
    Building2,
    Plus,
    Edit3,
} from "lucide-react";

const XFinanceSubscriptionForm = () => {
    const { user } = useUserAuth();
    const navigate = useNavigate();
    const { id } = useParams(); // Para editar suscripción existente
    const isEditing = Boolean(id);

    const [loading, setLoading] = useState(false);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [message, setMessage] = useState({ text: "", type: "" });
    const [categories, setCategories] = useState([]);

    const [formData, setFormData] = useState({
        nombre: "",
        categoria: "",
        cantidad: "",
        frecuenciaPago: "mensual",
        fechaInicio: "",
        fechaProximoPago: "",
        metodoPago: "",
        activa: true,
        notas: "",
        urlServicio: "",
        diasRecordatorio: 3,
    });

    const paymentMethods = [
        "Tarjeta de Crédito",
        "Tarjeta de Débito",
        "Transferencia Bancaria",
        "PayPal",
        "Domiciliación",
        "Efectivo",
    ];

    const frequencyOptions = [
        { value: "semanal", label: "Semanal" },
        { value: "mensual", label: "Mensual" },
        { value: "trimestral", label: "Trimestral" },
        { value: "semestral", label: "Semestral" },
        { value: "anual", label: "Anual" },
    ];

    // Verificar autenticación
    useEffect(() => {
        if (!user) {
            navigate("/login");
        }
    }, [user, navigate]);

    // Cargar categorías del usuario
    useEffect(() => {
        if (user) {
            loadCategories();
        }
    }, [user]);

    // Cargar datos de suscripción si es edición
    useEffect(() => {
        if (user && isEditing && id) {
            loadSubscriptionData();
        }
    }, [user, isEditing, id]);

    const loadCategories = async () => {
        if (!user) return;

        setLoadingCategories(true);
        try {
            const categoriesRef = collection(db, "subscriptions", user.uid, "subscriptionCategories");
            const q = query(categoriesRef, orderBy("nombre", "asc"));
            const querySnapshot = await getDocs(q);

            const categoriesData = [];
            querySnapshot.forEach((doc) => {
                categoriesData.push({
                    id: doc.id,
                    ...doc.data(),
                });
            });

            setCategories(categoriesData);
        } catch (error) {
            console.error("Error al cargar categorías:", error);
            setMessage({ text: "Error al cargar las categorías", type: "error" });
        } finally {
            setLoadingCategories(false);
        }
    };

    const loadSubscriptionData = async () => {
        if (!user || !id) return;

        try {
            const subscriptionRef = doc(db, "subscriptions", user.uid, "userSubscriptions", id);
            const subscriptionSnap = await getDoc(subscriptionRef);

            if (subscriptionSnap.exists()) {
                const data = subscriptionSnap.data();
                setFormData({
                    ...data,
                    fechaInicio: data.fechaInicio || "",
                    fechaProximoPago: data.fechaProximoPago || "",
                    cantidad: data.cantidad ? data.cantidad.toString() : "",
                });
            } else {
                setMessage({ text: "Suscripción no encontrada", type: "error" });
                navigate("/subscriptions");
            }
        } catch (error) {
            console.error("Error al cargar suscripción:", error);
            setMessage({ text: "Error al cargar los datos", type: "error" });
        }
    };

    const calculateNextPaymentDate = (startDate, frequency) => {
        const start = new Date(startDate);
        const today = new Date();

        let nextPayment = new Date(start);

        const addPeriod = (date, freq) => {
            switch (freq) {
                case "semanal":
                    date.setDate(date.getDate() + 7);
                    break;
                case "mensual":
                    date.setMonth(date.getMonth() + 1);
                    break;
                case "trimestral":
                    date.setMonth(date.getMonth() + 3);
                    break;
                case "semestral":
                    date.setMonth(date.getMonth() + 6);
                    break;
                case "anual":
                    date.setFullYear(date.getFullYear() + 1);
                    break;
            }
        };

        while (nextPayment <= today) {
            addPeriod(nextPayment, frequency);
        }

        return nextPayment.toISOString().split("T")[0];
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!user) {
            setMessage({ text: "Usuario no autenticado", type: "error" });
            return;
        }

        setLoading(true);

        try {
            const nextPaymentDate = formData.fechaProximoPago ||
                calculateNextPaymentDate(formData.fechaInicio, formData.frecuenciaPago);

            const subscriptionData = {
                ...formData,
                cantidad: parseFloat(formData.cantidad),
                fechaProximoPago: nextPaymentDate,
                updatedAt: new Date(),
                userId: user.uid,
            };

            if (isEditing) {
                // Actualizar suscripción existente
                const subscriptionRef = doc(db, "subscriptions", user.uid, "userSubscriptions", id);
                await updateDoc(subscriptionRef, subscriptionData);
                setMessage({ text: "Suscripción actualizada correctamente", type: "success" });
            } else {
                // Crear nueva suscripción
                const subscriptionsRef = collection(db, "subscriptions", user.uid, "userSubscriptions");
                await addDoc(subscriptionsRef, {
                    ...subscriptionData,
                    createdAt: new Date(),
                });
                setMessage({ text: "Suscripción creada correctamente", type: "success" });
            }

            // Redirigir después de un momento
            setTimeout(() => {
                navigate("/subscriptions");
            }, 1500);
        } catch (error) {
            console.error("Error al guardar suscripción:", error);
            setMessage({ text: "Error al guardar la suscripción", type: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    if (!user) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <Building2 className="w-16 h-16 text-blue-900 mx-auto mb-4" />
                    <p className="text-slate-600 font-medium">Acceso no autorizado</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <div className="bg-white border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between py-6">
                        <div className="flex items-center space-x-4">
                            <button
                                onClick={() => navigate("/subscriptions")}
                                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                            >
                                <ChevronLeft className="w-6 h-6 text-slate-600" />
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-slate-900">
                                    {isEditing ? "Editar Suscripción" : "Nueva Suscripción"}
                                </h1>
                                <p className="text-slate-600 font-medium">
                                    {isEditing
                                        ? "Modifique los datos de su suscripción"
                                        : "Complete la información de su nueva suscripción"
                                    }
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-gradient-to-r from-blue-900 to-blue-800 rounded-lg flex items-center justify-center">
                                <span className="text-white font-bold text-lg">X</span>
                            </div>
                            <span className="text-xl font-bold text-blue-900">XFinance</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Form Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
                    <div className="p-8">
                        <div className="space-y-8">
                            {/* Información Básica */}
                            <div>
                                <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                        <Building2 className="w-5 h-5 text-blue-600" />
                                    </div>
                                    Información Básica
                                </h2>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Nombre */}
                                    <div className="lg:col-span-2">
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Nombre de la Suscripción *
                                        </label>
                                        <input
                                            type="text"
                                            name="nombre"
                                            value={formData.nombre}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                            placeholder="Ej: Netflix, Microsoft Office, etc."
                                            required
                                        />
                                    </div>

                                    {/* Categoría */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Categoría *
                                        </label>
                                        <div className="flex gap-2">
                                            <select
                                                name="categoria"
                                                value={formData.categoria}
                                                onChange={handleInputChange}
                                                className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                                required
                                                disabled={loadingCategories}
                                            >
                                                <option value="">
                                                    {loadingCategories ? "Cargando..." : "Seleccionar categoría"}
                                                </option>
                                                {categories.map(category => (
                                                    <option key={category.id} value={category.nombre}>
                                                        {category.nombre}
                                                    </option>
                                                ))}
                                            </select>
                                            <button
                                                type="button"
                                                onClick={() => navigate("/subscription-categories")}
                                                className="px-4 py-3 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors flex items-center gap-2 font-medium"
                                                title="Gestionar categorías"
                                            >
                                                <Edit3 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* Monto */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Monto *
                                        </label>
                                        <div className="relative">
                                            <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <input
                                                type="number"
                                                name="cantidad"
                                                step="0.01"
                                                min="0"
                                                value={formData.cantidad}
                                                onChange={handleInputChange}
                                                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                                placeholder="0.00"
                                                required
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Configuración de Pagos */}
                            <div>
                                <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                        <Calendar className="w-5 h-5 text-green-600" />
                                    </div>
                                    Configuración de Pagos
                                </h2>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Frecuencia de Pago */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Frecuencia de Pago *
                                        </label>
                                        <select
                                            name="frecuenciaPago"
                                            value={formData.frecuenciaPago}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                            required
                                        >
                                            {frequencyOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Método de Pago */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Método de Pago
                                        </label>
                                        <select
                                            name="metodoPago"
                                            value={formData.metodoPago}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                        >
                                            <option value="">Seleccionar método</option>
                                            {paymentMethods.map(method => (
                                                <option key={method} value={method}>
                                                    {method}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Fecha de Inicio */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Fecha de Inicio *
                                        </label>
                                        <input
                                            type="date"
                                            name="fechaInicio"
                                            value={formData.fechaInicio}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                            required
                                        />
                                    </div>

                                    {/* Próximo Pago */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Próximo Pago
                                        </label>
                                        <input
                                            type="date"
                                            name="fechaProximoPago"
                                            value={formData.fechaProximoPago}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                        />
                                        <p className="text-xs text-slate-500 mt-1 font-medium">
                                            Si no se especifica, se calculará automáticamente basado en la frecuencia
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Información Adicional */}
                            <div>
                                <h2 className="text-xl font-bold text-slate-900 mb-6 flex items-center gap-3">
                                    <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                                        <Globe className="w-5 h-5 text-purple-600" />
                                    </div>
                                    Información Adicional
                                </h2>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* URL del Servicio */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            URL del Servicio
                                        </label>
                                        <div className="relative">
                                            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <input
                                                type="url"
                                                name="urlServicio"
                                                value={formData.urlServicio}
                                                onChange={handleInputChange}
                                                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                                placeholder="https://ejemplo.com"
                                            />
                                        </div>
                                    </div>

                                    {/* Días de Recordatorio */}
                                    <div>
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Días de Recordatorio
                                        </label>
                                        <div className="relative">
                                            <Bell className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                            <input
                                                type="number"
                                                name="diasRecordatorio"
                                                min="1"
                                                max="30"
                                                value={formData.diasRecordatorio}
                                                onChange={handleInputChange}
                                                className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                            />
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1 font-medium">
                                            Días antes del pago para mostrar recordatorio
                                        </p>
                                    </div>

                                    {/* Notas */}
                                    <div className="lg:col-span-2">
                                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                                            Notas Adicionales
                                        </label>
                                        <textarea
                                            name="notas"
                                            value={formData.notas}
                                            onChange={handleInputChange}
                                            rows={4}
                                            className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all font-medium"
                                            placeholder="Información adicional sobre la suscripción..."
                                        />
                                    </div>

                                    {/* Estado Activo */}
                                    <div className="lg:col-span-2">
                                        <div className="flex items-center space-x-3">
                                            <input
                                                type="checkbox"
                                                id="activa"
                                                name="activa"
                                                checked={formData.activa}
                                                onChange={(e) => setFormData(prev => ({ ...prev, activa: e.target.checked }))}
                                                className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                                            />
                                            <label htmlFor="activa" className="text-sm font-semibold text-slate-700">
                                                Suscripción activa
                                            </label>
                                        </div>
                                        <p className="text-xs text-slate-500 mt-1 font-medium">
                                            Las suscripciones inactivas no se incluyen en los cálculos totales
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Buttons */}
                            <div className="flex gap-4 pt-6 border-t border-slate-200">
                                <button
                                    type="button"
                                    onClick={() => navigate("/subscriptions")}
                                    className="flex-1 px-6 py-3 border border-slate-300 text-slate-700 rounded-lg font-semibold hover:bg-slate-50 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={handleSubmit}
                                    disabled={loading || !formData.nombre || !formData.categoria || !formData.cantidad || !formData.fechaInicio}
                                    className="flex-1 px-6 py-3 bg-blue-900 text-white rounded-lg font-semibold hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <Loader2 className="w-5 h-5 animate-spin" />
                                            {isEditing ? "Actualizando..." : "Guardando..."}
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-5 h-5" />
                                            {isEditing ? "Actualizar Suscripción" : "Crear Suscripción"}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toast Message */}
            {message.text && (
                <div
                    className={`fixed top-4 right-4 p-4 rounded-lg shadow-lg transition-all duration-300 z-50 max-w-md ${message.type === "error"
                        ? "bg-red-50 border border-red-200 text-red-800"
                        : "bg-green-50 border border-green-200 text-green-800"
                        }`}
                >
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {message.type === "error" ? (
                                <AlertCircle className="w-5 h-5 text-red-600" />
                            ) : (
                                <CheckCircle className="w-5 h-5 text-green-600" />
                            )}
                            <span className="font-semibold">{message.text}</span>
                        </div>
                        <button
                            onClick={() => setMessage({ text: "", type: "" })}
                            className="ml-4 p-1 hover:bg-white/50 rounded-full transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default XFinanceSubscriptionForm;